"use client";

import { Button } from "@west-tennessee-consulting/ui";
import { Building2, FolderOpen } from "lucide-react";

interface SaveAttachmentsDestinationProps {
  companyName: string | null;
  onBrowse: () => void;
}

/**
 * The default destination, stated plainly, with one way out of it.
 *
 * This is the whole point of the rework: the operator reads where the files are
 * going and confirms, instead of hunting the customer root for a folder whose
 * name looks right. The company came from the sender's CRM contact and the
 * folder itself is resolved server-side from `companies.google_drive_folder_id`,
 * so what is named here is the binding, not a guess.
 *
 * "Choose a different folder" is deliberately a quiet secondary action rather
 * than an equal option — flexibility that does not compete with the right
 * answer.
 */
export function SaveAttachmentsDestination({
  companyName,
  onBrowse,
}: SaveAttachmentsDestinationProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-4">
      <div className="flex items-start gap-3">
        <Building2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Saving to
          </p>
          <p className="truncate text-sm font-semibold text-foreground">
            {companyName ?? "This customer"}
          </p>
          <p className="text-xs text-muted-foreground">
            Their Google Drive folder &rarr; 08_Email-Attachments
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-2 h-auto px-2 py-1 text-xs"
        onClick={onBrowse}
      >
        <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
        Choose a different folder
      </Button>
    </div>
  );
}
